/** @jsx jsx */

import React, { useCallback, useEffect, useState } from "react";
import { jsx } from "@emotion/core";
import { Typography, message, Row, Col, Divider, Button, Switch, Checkbox } from "antd";
import { Formik, Form, FormikHelpers, FormikProps } from "formik";

//Components,hooks
import CustomButton from "../../../components/button";
import IntermediateScreen from "./IntermediateScreen";
import { FormInput, FormFileUpload } from "../../../components/formHelpers";
import AcceptRejectFold from "../acceptRejectFold";
import useEligibilityMetaData from "../../../hooks/auth/useEligibilityMetaData";
import useAPI from "../../../helperHooks/useAPI";
import useWepGenerateOTP from "../../../hooks/eligibility/useWepOTP";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useEligibilityDetails from "../../../hooks/eligibility/useEligibilityDetails";
import useNameAndImage from "../../../hooks/profile/useNameAndImage";
import useDocumentDownload from "../../../helperHooks/useDocumentDownload";

// Service, types, util methods, constants, styles and images.
import { EligibilityService } from "../../../services/eligibilityService";
import {
  EligibilityAmountDetails,
  LoanCalculationType,
  GstrDocumentType,
  // @ts-ignore
  EligibilityDetails,
  GetStartedLoanCalculation
} from "../../../services/eligibilityService/EligibilityService.type";
import { EligibilityDetailsState } from "../../../store/eligibility/state";
import { ServiceType } from "../../../services/ServiceType.data";
import {
  FileObject,
  FileStatus
} from "../../../components/formHelpers/FormFileUpload";
import { localNumber } from "../../../helper/Format";
import {
  eligibilityAmountFoldConstants as CONSTANTS,
  eligibilityAmountFoldValidationSchema as validationSchema,
  instantCalculationURL,
  GSTR2_SUPPORTED_EXTENSIONS,
  eligibilityAmountFoldConstants
} from "./EligibilityAmountFoldConstants";
import { eligibilityAmountFoldStyles } from "./EligibilityAmountFoldStyles";
import manualLoanImg from "../../../assets/manual-loan.svg";
import { routes } from "Routes";
import { Link } from "react-router-dom";
import useProfile from "hooks/profile/useProfile";
// import { EligibilityStatus } from "services/authService/AuthService.type";

const { Text, Title } = Typography;

interface Props {
  eligibilityDetails: EligibilityDetailsState;
  submitting: boolean;
}

export type EligibilityAmountDetailsState = EligibilityAmountDetails & {
  otp: string;
  gstin: string;
  attempts: number;
  verified: boolean;

  //files
  gstr1: FileObject[];
  gstr2a: FileObject[];
  gstr3b: FileObject[];
};

const getUploadedFiles = (
  eligibilityDetails: EligibilityDetailsState,
  type: GstrDocumentType
) =>
  eligibilityDetails.eligibilityAmountDetails.manualGstrDocuments
    .filter(file => file.docType === type)
    .map(
      (item): FileObject => ({
        id: item.id,
        status: FileStatus.Success,
        name: item.fileName,
        removed: false
      })
    );

const getInitialValues = (
  eligibilityDetails: EligibilityDetailsState,
  gstin: string
): EligibilityAmountDetailsState => ({
  ...eligibilityDetails.eligibilityAmountDetails,
  otp: "",
  gstin,
  attempts: 0,
  verified: false,

  gstr1: getUploadedFiles(eligibilityDetails, GstrDocumentType.GSTR1),
  gstr2a: getUploadedFiles(eligibilityDetails, GstrDocumentType.GSTR2A),
  gstr3b: getUploadedFiles(eligibilityDetails, GstrDocumentType.GSTR3B)
});

const EligibilityAmountFold: React.FC<Props> = (props: Props) => {
  const {
    eligibilityDetails,
    eligibilityDetails: {
      readMoreUrl,
      eligibilityAmountDetails: { eligibilityAmount, interestRate }
    }
  } = props;
  // console.log(props);
  const {
    getEligibilityDetails,
    changeLoanCalculationType,
    // @ts-ignore
    updateEligibilityDetails,
    startLoanApplication,
    eligibilityState: { loanDetails }
  } = useEligibilityDetails();
  // console.log(loanDetails);
  const { userName } = useNameAndImage();
  const { removeBorrowerConsent, giveBorrowerConsent } = useProfile();

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [userConsented, setUserConsented] = useState(false);
  //needs to be re-implemented @AC
  const [showMessage, setShowMessage] = useState(true);

  //To update "files" data on unmount
  useEffect(() => {
    return () => {
      getEligibilityDetails({});
    };
  }, [getEligibilityDetails]);

  const {
    disableEligibilityForms,
    disableManualCalculationForm,
    showEligibilityAmount,
    showInstantCalculationHelp,
    showAwaitingInPrincipleMessage,
    showAcceptReject,
    showInsufficientGSTRDataMessage,
    // @ts-ignore
    updateStatus,
    rejectedPrincipalAmount
  } = useLoanStatus();

  const {
    generateWepOtp,
    wepOtpData,
    verifyOtpData,
    verifyWepOtp
  } = useWepGenerateOTP();

  const {
    eligibilityMetaData: { eligibilityId, gstin }
  } = useEligibilityMetaData();

  const {
    callAPI: triggerManualFileVerification,
    loading: manualFileVerificationLoading
  } = useAPI(EligibilityService.triggerManualFileVerification);

  const getUploadConfig = useCallback(
    (docType: GstrDocumentType) => ({
      uploadServiceType: ServiceType.Eligibility,
      uploadPath: EligibilityService.uploadGstrConfig,
      uploadParams: { docType },
      deletePath: EligibilityService.deleteGstrConfig
    }),
    []
  );

  const { download, loading: downloading } = useDocumentDownload();

  const generateOTP = (
    data: EligibilityAmountDetailsState,
    formikHelpers: FormikHelpers<EligibilityAmountDetailsState>
  ) => {
    const { gstinLoginName } = data;
    if (gstinLoginName) {
      generateWepOtp(
        { gst_username: gstinLoginName },
        //OnSuccess
        () => {
          //Increasing the attempts on submit
          formikHelpers.setFieldValue("attempts", data.attempts + 1);
          message.success(CONSTANTS.feedback.otpSuccess);
        },
        error => formikHelpers.setFieldError("gstinLoginName", error.toString())
      );
    }
  };

  const handleVerifyOtp = (
    formikHelpers: FormikProps<EligibilityAmountDetailsState>
  ) => () => {
    const {
      values: { gstinLoginName, otp },
      setFieldValue,
      setFieldError
    } = formikHelpers;
    if (!gstinLoginName) {
      return;
    }
    verifyWepOtp(
      {
        otp,
        gst_username: gstinLoginName,
        gstin: getInitialValues(eligibilityDetails, gstin).gstin
      },
      //onSuccess
      _response => {
        setFieldValue("verified", true);
        //study the different conditions
        if (_response) {
          //changes made. Why was the function being called here?// - @AC
          // const temp:EligibilityDetails = {} as EligibilityDetails
          // updateEligibilityDetails(temp);
          // updateStatus({
          //   status: EligibilityStatus.AwaitingPrincipleAmount
          // });
          startLoanApplication({
            gst_username: gstinLoginName,
            gstin: getInitialValues(eligibilityDetails, gstin).gstin,
            eligibility_id: eligibilityId
          } as GetStartedLoanCalculation);
        }
      },
      error => setFieldError("otp", error)
    );
  };

  if (showAwaitingInPrincipleMessage && showMessage) {
    message.success(CONSTANTS.feedback.filesWillBeFetched)
    setShowMessage(false)
  }

  const setConsent = () => {
    userConsented ? removeBorrowerConsent() : giveBorrowerConsent();
    setUserConsented(!userConsented);
  }

  const handleSwitch = (
    formikHelpers: FormikProps<EligibilityAmountDetailsState>
  ) => (checked: boolean) => {
    const newLoanCalculationType = checked
      ? LoanCalculationType.manual
      : LoanCalculationType.instant;
    formikHelpers.setFieldValue("loanCalculationType", newLoanCalculationType);
    changeLoanCalculationType(newLoanCalculationType);
  };

  const handleSubmit = () => {
    triggerManualFileVerification(eligibilityId, () => {
      getEligibilityDetails({
        onSuccess: () => message.success(CONSTANTS.feedback.filesSubmitted)
      });
    });
  };

  const renderTitleRow = (
    formikHelpers: FormikProps<EligibilityAmountDetailsState>
  ) => {
    const {
      values: { loanCalculationType }
    } = formikHelpers;
    if (!loanCalculationType) {
      return <Text className="fold-title pr-5">{CONSTANTS.titles.title}</Text>;
    }
    return (
      <Row type="flex" className="align-items-center">
        <Text className="fold-title pr-5">{CONSTANTS.titles.title}</Text>
        <div className="pl-4">
          <Text
            className={`${loanCalculationType === LoanCalculationType.instant
              ? "selected-switch"
              : "unselected-switch"
              }`}
          >
            {CONSTANTS.titles.instantCalculation}
          </Text>
          <Switch
            className="mx-2"
            defaultChecked={loanCalculationType === LoanCalculationType.manual}
            onChange={handleSwitch(formikHelpers)}
            disabled={disableEligibilityForms}
          />
          <Text
            className={`${loanCalculationType === LoanCalculationType.manual
              ? "selected-switch"
              : "unselected-switch"
              }`}
          >
            {CONSTANTS.titles.fileUpload}
          </Text>
        </div>
      </Row>
    );
  };

  const renderInstantForm = (
    values: EligibilityAmountDetailsState,
    formikHelpers: FormikHelpers<EligibilityAmountDetailsState>
  ) => {
    const { setFieldValue, setFieldTouched } = formikHelpers;
    return (
      <React.Fragment>
        <FormInput
          name="gstinLoginName"
          mandatory
          label={CONSTANTS.labels.gstinLoginName}
          className="pb-3 pt-1"
          disabled={disableEligibilityForms}
          onChange={e => {
            //updating formik state
            setFieldValue("gstinLoginName", e.target.value);
            setFieldTouched("gstinLoginName");

            //resetting attempts on gstinLoginName change
            setFieldValue("attempts", 0);
          }}
        />
        <FormInput
          name="gstin"
          className="pb-4"
          label={CONSTANTS.labels.gstin}
          disabled
        />
        <CustomButton
          containerDivClassName="py-3"
          loading={wepOtpData.loading}
          disabled={wepOtpData.loading || disableEligibilityForms}
          onClick={() => {
            setFieldTouched("gstinLoginName");
            generateOTP(values, formikHelpers);
          }}
          aria-label="generate OTP"
        >
          {values.attempts
            ? CONSTANTS.buttons.regenerateOTP
            : CONSTANTS.buttons.generateOTP}
        </CustomButton>

        {!!values.attempts && (
          <FormInput
            name="otp"
            label={CONSTANTS.labels.otp}
            disabled={disableEligibilityForms}
          />
        )}
      </React.Fragment>
    );
  };

  const renderManualForm = (
    values: EligibilityAmountDetailsState,
    _: FormikHelpers<EligibilityAmountDetailsState>
  ) => (
    <React.Fragment>
      <Text className="manual-upload-note">
        {CONSTANTS.labels.manualUploadNote}
      </Text>
      <FormFileUpload
        name="gstr1"
        fileList={values.gstr1}
        multiple={true}
        config={getUploadConfig(GstrDocumentType.GSTR1)}
        label={CONSTANTS.labels.gstr1Label}
        className="mb-3"
        disableForm={disableManualCalculationForm}
        mandatory
      />

      <FormFileUpload
        name="gstr2a"
        fileList={values.gstr2a}
        multiple={true}
        acceptableFileExtensions={GSTR2_SUPPORTED_EXTENSIONS}
        config={getUploadConfig(GstrDocumentType.GSTR2A)}
        label={CONSTANTS.labels.gstr2aLabel}
        className="mb-3"
        disableForm={disableManualCalculationForm}
        mandatory
      />
      <FormFileUpload
        name="gstr3b"
        fileList={values.gstr3b}
        multiple={true}
        config={getUploadConfig(GstrDocumentType.GSTR3B)}
        label={CONSTANTS.labels.gstr3bLabel}
        className="mb-3"
        disableForm={disableManualCalculationForm}
        mandatory
      />
    </React.Fragment>
  );

  const renderInstantCalculationHelp = (
    <Col xs={24} md={10} offset={4}>
      <Text className="video-help-title pb-3 d-block">
        {CONSTANTS.labels.instantCalculationVideoTitle}
      </Text>
      <div className="video-container">
        <iframe
          title={CONSTANTS.labels.instantCalculationVideoTitle}
          src={instantCalculationURL}
          className="video-help-iframe"
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    </Col>
  );

  const renderEligibilityStatus = () => {
    if (
      !showEligibilityAmount &&
      !showInsufficientGSTRDataMessage &&
      !showAwaitingInPrincipleMessage &&
      !loanDetails.calculating &&
      !loanDetails.calculated
    ) {
      return null;
    }
    return (
      <React.Fragment>
        <Col span={2} offset={2}>
          <Divider type="vertical" dashed />
        </Col>
        <Col xs={24} md={10} className="d-flex flex-column align-items-left">
          {rejectedPrincipalAmount && (
            <Title
              level={4}
              className="eligibility-label rejected-principal-amount"
            >
              {CONSTANTS.labels.rejectedPrincipalAmount(
                userName,
                localNumber(eligibilityAmount)
              )}
            </Title>
          )}
          {(loanDetails.calculated || (showEligibilityAmount && !rejectedPrincipalAmount)) && (
            <React.Fragment>
              <Title level={4} className="eligibility-label">
                {CONSTANTS.labels.eligibilityInfo(userName)}
              </Title>
              <Title
                level={3}
                className="eligibility-amount align-self-center d-inline-block px-4 py-1"
              >
                Rs {localNumber(eligibilityAmount, '0')}
              </Title>
              <Text className="eligibility-note d-block pt-2">
                {interestRate
                  ? CONSTANTS.labels.eligibilityInfoSuffix(interestRate)
                  : ""}
              </Text>
              <br />
              {CONSTANTS.labels.eligibilityInfoSteps.map((info, index) => (
                <Text className="eligibility-note d-block" key={index}>
                  {index + 1}. {info}
                </Text>
              ))}
              <Text className="eligibility-note d-block pt-4">
                {CONSTANTS.labels.eligibilityInfoNote}
                <Button
                  type="link"
                  loading={downloading}
                  onClick={() => {
                    download(readMoreUrl, true);
                  }}
                >
                  {CONSTANTS.labels.readMore}
                </Button>
              </Text>
            </React.Fragment>
          )}
          {(loanDetails.calculating || showAwaitingInPrincipleMessage) && (
            <React.Fragment>
              <Title level={4} className="eligibility-label">
                {CONSTANTS.labels.eligibilityInProgress(userName)}
              </Title>
              <img className="pt-2" src={manualLoanImg} alt="Manual Upload" />
              <Text className="eligibility-note d-block pt-4">
                {CONSTANTS.labels.eligibilityInfoNote}
              </Text>
            </React.Fragment>
          )}
          {showInsufficientGSTRDataMessage && (
            <React.Fragment>
              <Title level={4} className="eligibility-label">
                {CONSTANTS.labels.insufficientGSTRData}
              </Title>
            </React.Fragment>
          )}
        </Col>
      </React.Fragment>
    );
  };

  const renderRightColumn = (values: EligibilityAmountDetailsState) => (
    <React.Fragment>
      {renderEligibilityStatus()}
      {showInstantCalculationHelp &&
        values.loanCalculationType === LoanCalculationType.instant &&
        renderInstantCalculationHelp}
    </React.Fragment>
  );

  const renderUserConsent = (
    formikHelpers: FormikProps<EligibilityAmountDetailsState>) => {
    const {
      values: { attempts, otp }
    } = formikHelpers;
    if (!disableEligibilityForms && !!attempts && otp) {
      return (
        <div className="d-flex justify-content-center align-items-center user-consent-wrapper">
          <Checkbox
            name='user-consent'
            checked={!submitDisabled}
            onChange={() => { setSubmitDisabled(!submitDisabled); setConsent() }}
          >
            <Text className="mandatory">{eligibilityAmountFoldConstants.consentText} <Link target='_blank' to={routes.static.bct} title={eligibilityAmountFoldConstants.borrowerConsent}>{eligibilityAmountFoldConstants.borrowerConsent}</Link>, <Link target='_blank' to={routes.static.pp} title={eligibilityAmountFoldConstants.privacyPolicy}>{eligibilityAmountFoldConstants.privacyPolicy}</Link> & <Link target='_blank' to={routes.static.tou} title={eligibilityAmountFoldConstants.termsUse}>{eligibilityAmountFoldConstants.termsUse}</Link></Text>
          </Checkbox>
        </div>
      )
    }
    return null;
  }

  const renderFooter = (
    formikHelpers: FormikProps<EligibilityAmountDetailsState>,
    submitDisabled: boolean = false
  ) => {
    if (showEligibilityAmount || showAwaitingInPrincipleMessage) {
      return null;
    }
    const {
      values: { loanCalculationType, attempts, otp }
    } = formikHelpers;
    if (
      loanCalculationType === LoanCalculationType.manual &&
      !showAwaitingInPrincipleMessage
    ) {
      return (
        <CustomButton
          htmlType="submit"
          type="primary"
          containerDivClassName="justify-content-md-center mb-5"
          loading={manualFileVerificationLoading}
          disabled={manualFileVerificationLoading}
          aria-label="submit files"
          aria-disabled={manualFileVerificationLoading}
        >
          {CONSTANTS.buttons.submit}
        </CustomButton>
      );
    }
    if (!!attempts && otp) {
      return (
        <CustomButton
          type="primary"
          containerDivClassName="justify-content-md-center mb-5"
          loading={verifyOtpData.loading}
          disabled={verifyOtpData.loading || submitDisabled}
          onClick={handleVerifyOtp(formikHelpers)}
          aria-label="verify OTP"
          aria-disabled={verifyOtpData.loading}
        >
          {CONSTANTS.buttons.submit}
        </CustomButton>
      );
    }
    return null;
  };

  return (
    <section css={eligibilityAmountFoldStyles}>
      <Formik
        initialValues={getInitialValues(eligibilityDetails, gstin)}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
      >
        {formikHelpers => {
          const { values, setFieldValue } = formikHelpers;

          return (
            <Form>
              <div className="d-flex flex-column">
                {/* Row contains the fold title and the Switch. */}
                {renderTitleRow(formikHelpers)}
                {values.loanCalculationType ? (
                  <Row className="pt-4" type="flex">
                    {/* Left Column containing Instant or Manual Calculation form. */}
                    <Col xs={24} md={10}>
                      {values.loanCalculationType ===
                        LoanCalculationType.instant
                        ? renderInstantForm(values, formikHelpers)
                        : renderManualForm(values, formikHelpers)}
                    </Col>
                    {/* Right Column containing status or help video */}
                    {renderRightColumn(values)}
                    {!showInsufficientGSTRDataMessage && (<React.Fragment>
                      <Col span={24} className="pt-5">
                        {renderUserConsent(formikHelpers)}
                      </Col>
                      <Col span={24} className="pt-5">
                        {/* Render Submit buttons for these forms. */}
                        {renderFooter(formikHelpers, submitDisabled)}
                      </Col></React.Fragment>)}
                  </Row>
                ) : (
                  <IntermediateScreen
                    setFieldValue={setFieldValue}
                    changeLoanCalculationType={changeLoanCalculationType}
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      {showAcceptReject && (
        <Col span={24} className="pt-5">
          <AcceptRejectFold eligibilityDetails={eligibilityDetails} />
        </Col>
      )}
    </section>
  );
};

export default EligibilityAmountFold;
